import { Heading2, BodyM, Caption, Button, Small } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import { Dispatch, SetStateAction } from 'react'

import BellNotification from '@/components/Icon/BellNotification'
import { Translated } from '@/components/Translated'
import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { ContextValue, useCities } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import Bell from '@/design-lib/components/Icons/Bell'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { profilePath } from '@/i18n-routes'
import { FavouriteSearchOrigin } from '@/lib/api-gluglu-open'
import { ListingCate } from '@/page-commons/listingBySlug'
import { marginBottom, paddingBottom, marginRight } from '@/shared-styles/styles.css'
import { FilterZone, GeoFilter } from '@/types/filter'
import { Filter } from '@/types/graphql-generated-types'
import { buildRelativeLocalizedLink } from '@/utils/navigation'
import { handleNewListingsAlertClick } from '@/utils/tracking/eventsHandlers'

import { saveSearchContent, saveSearchContentTop, geoInfo } from '../../styles.css'
import { ContentWrapper } from '../Common/ContentWrapper'
import { FiltersBoxListingContact } from '../ListingContact/FiltersBoxListingContact'
import { EmptyFiltersBox } from '../SearchResults/EmptyFiltersBox'
import { FiltersBoxSearchResults } from '../SearchResults/FiltersBoxSearchResults'

import { ContentLogin } from './ContentLogin'
import { GeoInfo } from './GeoInfo'

type Props = {
  actualZoneFilterAreas?: GeoFilter[]
  cities: ContextValue
  filters?: Filter
  geoSlugs?: string[]
  handleSubmitUserFavoriteSearch: VoidFunction
  listing?: ListingCate
  origin: FavouriteSearchOrigin
  setIsLoadingFavouriteSearch: Dispatch<SetStateAction<boolean>>
  setShowContentLogin?: Dispatch<SetStateAction<boolean>>
  userEmail?: string
}

export const ContentDefault: React.FC<Props> = ({
  actualZoneFilterAreas,
  cities,
  filters,
  geoSlugs,
  handleSubmitUserFavoriteSearch,
  listing,
  origin,
  setIsLoadingFavouriteSearch,
  setShowContentLogin,
  userEmail,
}) => {
  const { lang, t } = useTranslation()
  const device = useDevice()
  const { user } = useAuthentication()
  const { closeBottomSheet, openBottomSheet } = useBottomSheet()

  const { byZone, ...filtersWithoutZone } = filters ?? {}

  return (
    <ContentWrapper className={sprinkles({ padding: 'l' })} origin={origin}>
      <div
        className={sprinkles({
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          marginBottom: { desktop: 'xl', mobile: 'l' },
        })}
      >
        <BellNotification className={marginBottom.s} ellipseColor="orange600" />
        <Heading2 alignment="center">
          <Translated i18Key="save-search:Modal_Title" />
        </Heading2>
      </div>
      <div className={saveSearchContent}>
        <div className={saveSearchContentTop}>
          <BodyM>{t('save-search:Modal_Zone_intro')}</BodyM>
          {origin === 'SEARCH_RESULTS' ? (
            <>
              <GeoInfo
                activeZones={actualZoneFilterAreas.filter((item): item is FilterZone => item.kind === 'zone')}
                area={
                  cities.router.city?.translatedCityName ??
                  `${cities.router.area.translatedCityName} ${t('common:province')}`
                }
              />
              {Object.keys(filtersWithoutZone).length > 0 ? (
                <FiltersBoxSearchResults filters={filters} />
              ) : (
                <EmptyFiltersBox />
              )}
            </>
          ) : (
            <>
              <BodyM strong className={geoInfo}>
                {`${
                  cities.router.city?.translatedCityName ??
                  `${cities.router.area.translatedCityName} ${t('common:province')}`
                }${listing.detail.zone ? `, ${listing.detail.zone.label}` : ''}`}
              </BodyM>

              <FiltersBoxListingContact listing={listing} />
            </>
          )}
        </div>

        <Caption alignment="center" className={paddingBottom.m}>
          <span aria-label="coffee" className={marginRight.xs} role="img">
            ☕️
          </span>{' '}
          {t('save-search:Modal_Form_NotificationsMessage')}
        </Caption>

        <Button
          center
          className={marginBottom.m}
          data-testid="save-search-button"
          Icon={(props) => <Bell {...props} />}
          label={t('save-search:Modal_CTA')}
          minWidth={160}
          onClick={async () => {
            handleNewListingsAlertClick(
              'ModalButtonCTA',
              origin === 'SEARCH_RESULTS' ? 'ListingsFeed' : 'ListingDetail'
            )
            if (user) {
              handleSubmitUserFavoriteSearch()
            } else if (device === 'mobile') {
              openBottomSheet(
                origin === 'SEARCH_RESULTS' ? (
                  <ContentLogin
                    actualZoneFilterAreas={actualZoneFilterAreas}
                    filters={filters}
                    geoSlugs={geoSlugs}
                    origin={origin}
                    setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
                    onLoggedIn={() => closeBottomSheet()}
                  />
                ) : (
                  <ContentLogin
                    listing={listing}
                    origin={origin}
                    setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
                    userEmail={userEmail}
                    onLoggedIn={() => closeBottomSheet()}
                  />
                ),
                { minHeight: 610 }
              )
            } else {
              setShowContentLogin(true)
            }
          }}
        />

        {user && (
          <Small alignment="center" color="greyscale500">
            {t('save-search:Modal_AuthUser_Text')} {user.email}
            {' - '}
            <a
              className={sprinkles({ fontWeight: 600, textDecoration: 'underline' })}
              href={buildRelativeLocalizedLink(profilePath, lang)}
              onClick={() => handleNewListingsAlertClick('Profile')}
            >
              {t('save-search:Modal_AuthUser_Link')}
            </a>
          </Small>
        )}
      </div>
    </ContentWrapper>
  )
}
